import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { F } from '../../styles/theme';

const RED = '#E4544A';   // Koralle (Marken-Akzent)
const INK = '#1B1B1E';   // dunkle Kachel

/** App-Icon-Zeichen: korallenes rundes Quadrat, 2×2-Kachelraster (3 weiß,
 *  BR ink) mit korallenem „+". Identisch zu brand/red-app-icon-coral.svg /
 *  public/favicon.svg. Mark sitzt 0.66 zentriert (Safe-Zone fürs Maskable-Icon). */
export function RedSymbol({ size = 32 }) {
  return (
    <svg viewBox="0 0 160 160" width={size} height={size} style={{ display: 'block', flexShrink: 0 }}>
      <rect width="160" height="160" rx="36" fill={RED} />
      <g transform="translate(80 80) scale(0.66) translate(-80 -80)">
        <rect x="18" y="18" width="58" height="58" rx="17" fill="#FFFFFF" />
        <rect x="84" y="18" width="58" height="58" rx="17" fill="#FFFFFF" />
        <rect x="18" y="84" width="58" height="58" rx="17" fill="#FFFFFF" />
        <rect x="84" y="84" width="58" height="58" rx="17" fill={INK} />
        <rect x="97" y="109.5" width="32" height="7" rx="3.5" fill={RED} />
        <rect x="109.5" y="97" width="7" height="32" rx="3.5" fill={RED} />
      </g>
    </svg>
  );
}

/** Nur die Mark (ohne Kästchen) — das 2×2-Kachelraster monochrom in `color`,
 *  das „+" als Negativ-Aussparung (evenodd). `color` per Default weiß; auf
 *  neutralen Flächen besser theme-abhängig (T.text) übergeben. */
export function RedMark({ size = 26, color = '#FFFFFF' }) {
  return (
    <svg viewBox="14 14 132 132" width={size} height={size} style={{ display: 'block', flexShrink: 0 }} aria-hidden>
      <path fillRule="evenodd" clipRule="evenodd" fill={color} d="
        M35 18 h24 a17 17 0 0 1 17 17 v24 a17 17 0 0 1 -17 17 h-24 a17 17 0 0 1 -17 -17 v-24 a17 17 0 0 1 17 -17 z
        M101 18 h24 a17 17 0 0 1 17 17 v24 a17 17 0 0 1 -17 17 h-24 a17 17 0 0 1 -17 -17 v-24 a17 17 0 0 1 17 -17 z
        M35 84 h24 a17 17 0 0 1 17 17 v24 a17 17 0 0 1 -17 17 h-24 a17 17 0 0 1 -17 -17 v-24 a17 17 0 0 1 17 -17 z
        M101 84 h24 a17 17 0 0 1 17 17 v24 a17 17 0 0 1 -17 17 h-24 a17 17 0 0 1 -17 -17 v-24 a17 17 0 0 1 17 -17 z
        M109.5 97 H116.5 V109.5 H129 V116.5 H116.5 V129 H109.5 V116.5 H97 V109.5 H109.5 Z" />
    </svg>
  );
}

/** plan: 'free' | 'premium' | 'vip' → sub-label fürs LOGO (Free bleibt leer,
 *  damit das Logo nicht zu „red. Free" wird). */
export function planSub(plan) {
  if (plan === 'vip') return 'VIP';
  if (plan === 'premium') return 'Premium';
  return '';
}

/** plan → IMMER ein sichtbares Label (Free/Premium/VIP) für Plan-Badges,
 *  die als „red. <Plan>"-Wordmark dargestellt werden. */
export function planLabel(plan) {
  if (plan === 'vip') return 'VIP';
  if (plan === 'premium') return 'Premium';
  return 'Free';
}

export function RedWordmark({ sub = '', size = 18, onRed = false, color = null }) {
  const { isDark } = useTheme();
  // onRed: Wordmark sitzt auf rotem Hintergrund → Punkt als gedecktes Weiß,
  // Text voll weiß (siehe feedback_wordmark_font_rule.md).
  // color: expliziter Text-Override (z.B. auf ink-/Ton-Button) → Text in dieser
  // Farbe, Punkt bleibt Marken-Rot (Calm: Rot nur als Akzent).
  const mainColor = color || (onRed ? '#fff' : (isDark ? '#fff' : '#0C0C0F'));
  const dotColor = (!color && onRed) ? 'rgba(255,255,255,0.65)' : RED;
  const subColor = color
    ? color
    : onRed
      ? '#fff'
      : (sub === 'VIP' ? '#D4D5DE' : isDark ? '#fff' : '#0C0C0F');

  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: size * 0.3, lineHeight: 1 }}>
      <span
        style={{
          fontFamily: F.brand,
          fontSize: size,
          fontWeight: 800,
          letterSpacing: '-0.04em',
        }}
      >
        <span style={{ color: mainColor }}>red</span>
        {/* Punkt = korallenes Kachel-Quadrat (neues Logo), bündig auf Grundlinie */}
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: size * 0.18,
            height: size * 0.18,
            marginLeft: size * 0.09,
            background: dotColor,
            borderRadius: size * 0.036,
            verticalAlign: 'baseline',
          }}
        />
      </span>
      {sub && (
        <span
          style={{
            color: subColor,
            fontFamily: F.brand,
            fontSize: size,
            fontWeight: 700,
            letterSpacing: '-0.04em',
            opacity: sub === 'VIP' ? 1 : 0.94,
          }}
        >
          {sub}
        </span>
      )}
    </div>
  );
}

export function BracketMark({ color = '#fff', size = 10, opacity = 0.85, pos = 'tl' }) {
  const offsets = {
    tl: { top: 6, left: 6 },
    tr: { top: 6, right: 6 },
    bl: { bottom: 6, left: 6 },
    br: { bottom: 6, right: 6 },
  }[pos] || { top: 6, left: 6 };
  return (
    <div style={{ position: 'absolute', width: size, height: size, pointerEvents: 'none', opacity, ...offsets }}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: size, height: 1.2, background: color, borderRadius: 0.5 }} />
      <div style={{ position: 'absolute', top: 0, left: 0, width: 1.2, height: size, background: color, borderRadius: 0.5 }} />
    </div>
  );
}
